'use client';

import Image from "next/image";
import Button from "../ui/Button";
import Link from "next/link";
import ChevronRightIcon from "../icons/ChevronRightIcon";
import { categoryCoverKey, categoryImageSrc } from "@/lib/search-hero";

type Category = {
  title: string;
  image: string;
  link: string;
};

/**
 * Artwork always comes from the category, never from the saved tile.
 *
 * The admin panel used to carry a thumbnail field per tile. It was a second
 * source of truth that could only ever drift from the /categories page and the
 * search hero, which both derive their image the same way this does. Deriving it
 * here means a category looks identical everywhere by construction.
 */
function tileImage(category: Category): string {
  const query = category.link.includes("?")
    ? category.link.slice(category.link.indexOf("?"))
    : "";
  const linked = new URLSearchParams(query).get("category");
  return categoryImageSrc(categoryCoverKey(linked || category.title));
}

type CategorySectionProps = {
  categories: Category[];
  /** Merged onto inner content wrapper (e.g. `!px-0` when parent supplies page gutter). */
  contentClassName?: string;
};

export default function CategorySection({ categories, contentClassName }: CategorySectionProps) {
  return (
    <section className="w-full" aria-label="Product Categories">
      <div
        className={`w-full px-4 sm:px-6 lg:px-8${contentClassName ? ` ${contentClassName}` : ''}`}
      >
        <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-6">
          Shop by category
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
          {categories.map((category) => (
            <div
              key={category.title}
              className="group relative aspect-[328/180] rounded-2xl overflow-hidden"
            >
              <div className="absolute inset-0">
                <Image
                  src={tileImage(category)}
                  alt={`Shop ${category.title} cannabis products at Dank Cannabis`}
                  fill
                  quality={60}
                  className="object-cover"
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                  loading="lazy"
                />
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(23, 22, 90, 0.65) 0%, rgba(52, 59, 145, 0.35) 100%)",
                  }}
                ></div>
              </div>
              <div className="relative z-10 h-full flex flex-col justify-between sm:p-6 p-4">
                <h3 className="text-2xl font-bold text-constant-white">
                  {category.title}
                </h3>
                <Button asChild variant="glass" className="w-max !px-2.5">
                  <Link href={category.link} aria-label={`Shop ${category.title}`}>
                    <ChevronRightIcon size={16} color="currentColor" />
                  </Link>
                </Button>
              </div>
            </div>
          ))}

          <div className="group relative aspect-[328/180] rounded-2xl overflow-hidden bg-blue-80">
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: "url(/media/categories/background-vector.svg)",
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            ></div>
            <div className="relative z-10 h-full flex flex-col justify-between sm:p-6 p-4">
              <h3 className="sm:text-2xl text-body-20 font-bold text-constant-white max-w-80">
                Dive into all available categories we offer!
              </h3>
              <Button asChild variant="glass" size="lg" className="sm:w-max w-full rounded-xl">
                <Link href="/categories">
                  View all categories
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

